import { map } from "lodash-es";
import { serviceURL } from "../../../utils/service-url";

import type { RecentlyEditedType } from "../../dashboard/hooks/use-recently-edited";
import type { AtlasSearchResult, ContentSearchResult } from "./types";

export class AtlasV2Service {
  async search(
    getToken: () => Promise<string>,
    q: string,
    filters?: {
      program?: string[];
      status?: string[];
      channel?: string[];
    },
  ): Promise<{ data: AtlasSearchResult["moment"][]; error: null } | { data: null; error: Error }> {
    const query = new URLSearchParams({ q: q.trim() });

    map(filters ?? {}, (values, key) => values?.forEach((value) => query.append(key, value)));

    const response = await fetch(`${serviceURL("atlas-v2")}moment/search?${query.toString()}`, {
      headers: {
        Authorization: `Bearer ${await getToken()}`,
      },
    });
    if (!response.ok)
      return { data: null, error: new Error((await response.text()) ?? "Unknown error") };

    const data: { results: AtlasSearchResult[] } = await response.json();
    return {
      data: data.results.map((x) => x.moment),
      error: null,
    };
  }

  /**
   * The Communications, Surveys and Digital Assets matching `q`, best first, a page at a time. With
   * `entityType`, only that kind.
   */
  async searchContent(
    getToken: () => Promise<string>,
    q: string,
    {
      count,
      offset,
      entityType,
      signal,
    }: {
      count: number;
      offset: number;
      entityType?: RecentlyEditedType;
      /** Cancels the request, as when a newer search replaces it. */
      signal?: AbortSignal;
    },
  ): Promise<{ data: ContentSearchResult[]; error: null } | { data: null; error: Error }> {
    const query = new URLSearchParams({
      q: q.trim(),
      count: String(count),
      offset: String(offset),
      ...(entityType && { type: entityType }),
    });

    const response = await fetch(`${serviceURL("atlas-v2")}content/search?${query.toString()}`, {
      headers: {
        Authorization: `Bearer ${await getToken()}`,
      },
      signal,
    });
    if (!response.ok)
      return { data: null, error: new Error((await response.text()) ?? "Unknown error") };

    const data: { results: ContentSearchResult[] } = await response.json();
    return { data: data.results, error: null };
  }
}
